import { useEffect } from 'react'
import {
  Grid,
  Paper,
  Typography,
  Box,
  useTheme,
  Stack,
} from "@mui/material";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import { Tooltip } from "@mui/material";
import { DeletedIcon, NewUserIcon, PendingTicketsIcon, UnreadChatsIcon, UsersIcon,PaymentIcon, VerificationIcon } from "../../icons";
import { useDispatch, useSelector } from 'react-redux'
import { useState } from 'react'
import { fetchDashboardStatsData, fetchDashboardUnreadBotChatStatsData } from '../../api/region'
import DashboardCardSkeleton from './DashboardCardSkeleton';
import { useNavigate } from 'react-router-dom';
import { setResetFilter, setStatsTypeFilter } from '../app-container/reducer/filterSlice';
import moment from 'moment';
import { STATUS_DESCRIPTION } from '../../utils/memberStatus';

const TrendText = ({ trend, trendText, positive = true }) => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        fontSize: "0.75rem",
        fontWeight: 500,
        mt: 0.5,
        gap: 0.5
      }}
    >
      <TrendingUpIcon
        sx={{
          fontSize: 16,
          mr: 0.5,
          transform: positive ? "rotate(0deg)" : "rotate(180deg)",
          color: positive ? theme.palette.success.main : theme.palette.error.main,
        }}
      />
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, }}>
        <Typography variant="span" sx={{ color: positive ? theme.palette.success.main : theme.palette.error.main, }}>{trend}</Typography>
        <Typography variant="span" sx={{ fontSize: '12px', color: '#848484', fontWeight: '400', }}>{trendText}</Typography>
      </Box>
    </Box>
  );
};

const chatStatsCard = {
  title: "UNREAD BOT CHATS",
  mainValue: "Loading...",
  trend: "",
  // trendText: 'previous data',
  icon: <UnreadChatsIcon fontSize="small" />,
  trendPositive: false,
  tooltipText: "Messages sent by users that have not yet been responded to by the chat bot.",
  statsType: "unread_bot_chats",
  navigateUrl: "",
  filters: { filterType: "Unread Messages" }
}

export default function DashboardCards() {
  const theme = useTheme();
  const dispatch = useDispatch()
  const { filter } = useSelector((state) => state);
  const [isLoading, setIsLoading] = useState(true);
  const [chatCardsData, setChatCardsData] = useState(chatStatsCard);
  const [cardsData, setCardsData] = useState([]);
  const navigate = useNavigate();

  const fetchData = async () => {
    try {
      setIsLoading(true);
      const data = await fetchDashboardStatsData(filter);
      if(!data) return
      // console.log('data', data);
      const responseSet = [
        {
          title: "TOTAL USERS",
          mainValue: data?.totalUsers?.value || 0,
          trend: data?.totalUsers?.change > 0
            ? `+${data.totalUsers.change}`
            : data?.totalUsers?.change < 0
              ? `-${Math.abs(data.totalUsers.change)}`
              : "",
          // trendText: 'previous data',
          icon: <UsersIcon fontSize="small" />,
          trendPositive: data?.totalUsers?.change < 0 ? false : true,
          tooltipText: 'Total number of user accounts currently registered on the platform, including all profile statuses',
          statsType:"total_users",
          filters:{dateRange:{startDate:moment('2020-01-01'), endDate:moment()}},
          navigateUrl:"/user-management"
        },
        {
          title: "NEW SIGNUPS",
          mainValue: data?.newSignups?.value || 0,
          trend: data?.newSignups?.change > 0
            ? `+${data.newSignups.change}`
            : data?.newSignups?.change < 0
              ? `-${Math.abs(data.newSignups.change)}`
              : "",
          trendText: 'previous data',
          icon: <NewUserIcon fontSize="small" />,
          trendPositive: data?.newSignups?.change < 0 ? false : true,
          tooltipText: 'Number of new users who created an account during the selected date range.',
          statsType:"new_signup",
          navigateUrl:"/user-management"
        },
        {
          title: "COMPLETE SIGNUP",
          mainValue: data?.profileCompleted?.value || 0,
          trend: data?.profileCompleted?.change > 0
            ? `+${data.profileCompleted.change}`
            : data?.profileCompleted?.change < 0
              ? `-${Math.abs(data.profileCompleted.change)}`
              : "",
          trendText: 'previous data',
          icon: <NewUserIcon fontSize="small" />,
          trendPositive: data?.profileCompleted?.change < 0 ? false : true,
          tooltipText: 'Number of users who completed their profile',
          statsType:"complete_signup",
          navigateUrl:"/user-management",
        },
        {
          title: "APPROVED",
          mainValue: data?.approvedUsers?.value || 0,
          trend: data?.approvedUsers?.change > 0
            ? `+${data.approvedUsers.change}`
            : data?.approvedUsers?.change < 0
              ? `-${Math.abs(data.approvedUsers.change)}`
              : "",
          trendText: 'previous data',
          icon: <UsersIcon fontSize="small" />,
          trendPositive: data?.approvedUsers?.change < 0 ? false : true,
          tooltipText: STATUS_DESCRIPTION.approved,
          statsType:"approved_users",
          navigateUrl:"/user-management",
        },
        {
          title: "INACTIVE",
          mainValue: data?.inactiveUsers?.value || 0,
          trend: data?.inactiveUsers?.change > 0
            ? `+${data.inactiveUsers.change}`
            : data?.inactiveUsers?.change < 0
              ? `-${Math.abs(data.inactiveUsers.change)}`
              : "",
          trendText: 'previous data',
          icon: <UsersIcon fontSize="small" />,
          trendPositive: data?.inactiveUsers?.change < 0 ? false : true,
          tooltipText: STATUS_DESCRIPTION.inactive,
          statsType:"inactive_users",
          navigateUrl:"/user-management",
        },
        {
          title: "GHOST USERS",
          mainValue: data?.ghostUsers?.value || 0,
          trend: data?.ghostUsers?.change > 0
            ? `+${data.ghostUsers.change}`
            : data?.ghostUsers?.change < 0
              ? `-${Math.abs(data.ghostUsers.change)}`
              : "",
          trendText: 'previous data',
          icon: <UsersIcon fontSize="small" />,
          trendPositive: data?.ghostUsers?.change < 0 ? false : true,
          tooltipText: STATUS_DESCRIPTION.ghost,
          statsType:"ghost_users",
          navigateUrl:"/user-management",
        },
        chatStatsCard,
        {
          title: "DELETED PROFILES",
          mainValue: data?.deletedProfiles?.value || 0,
          trend: data?.deletedProfiles?.change > 0
            ? `+${data.deletedProfiles.change}`
            : data?.deletedProfiles?.change < 0
              ? `-${Math.abs(data.deletedProfiles.change)}`
              : "",
          trendText: 'previous data',
          icon: <DeletedIcon fontSize="small" />,
          trendPositive: data?.deletedProfiles?.change < 0 ? false : true,
          tooltipText: "Accounts permanently deleted by users. These profiles cannot be recovered and require re-registration.",
          statsType:"deleted_profiles",
          navigateUrl:"/deleted-accounts"
        },
        {
          title: "DEACTIVATED ACCOUNTS",
          mainValue: data?.deactivatedAccounts?.value || 0,
          trend: data?.deactivatedAccounts?.change > 0
            ? `+${data.deactivatedAccounts.change}`
            : data?.deactivatedAccounts?.change < 0
              ? `-${Math.abs(data.deactivatedAccounts.change)}`
              : "",
          trendText: 'previous data',
          icon: <DeletedIcon fontSize="small" />,
          trendPositive: data?.deactivatedAccounts?.change < 0 ? false : true,
          tooltipText: 'User accounts that have been temporarily disabled either by the user These may be reactivated later.',
          statsType:"deactivated_accounts",
          navigateUrl:"/user-management"
        },
        {
          title: "SUSPENDED ACCOUNTS",
          mainValue: data?.suspendedAccounts?.value || 0,
          trend: data?.suspendedAccounts?.change > 0
            ? `+${data.suspendedAccounts.change}`
            : data?.suspendedAccounts?.change < 0
              ? `-${Math.abs(data.suspendedAccounts.change)}`
              : "",
          trendText: 'previous data',
          icon: <DeletedIcon fontSize="small" />,
          trendPositive: data?.suspendedAccounts?.change < 0 ? false : true,
          tooltipText: 'Accounts suspended/banned by an admin (a distinct state from deactivated).',
          statsType:"suspended_users",
          navigateUrl:"/user-management"
        },
        {
          title: "HIDDEN",
          mainValue: data?.hiddenByAdmin?.value || 0,
          trend: data?.hiddenByAdmin?.change > 0
            ? `+${data.hiddenByAdmin.change}`
            : data?.hiddenByAdmin?.change < 0
              ? `-${Math.abs(data.hiddenByAdmin.change)}`
              : "",
          trendText: 'previous data',
          icon: <DeletedIcon fontSize="small" />,
          trendPositive: data?.hiddenByAdmin?.change < 0 ? false : true,
          tooltipText: STATUS_DESCRIPTION.hidden,
          statsType:"hidden_by_admin",
          navigateUrl:"/user-management"
        },
        {
          title: "PENDING TICKETS",
          mainValue: data?.pendingTickets?.value || 0,
          trend: data?.pendingTickets?.change > 0
            ? `+${data.pendingTickets.change}`
            : data?.pendingTickets?.change < 0
              ? `-${Math.abs(data.pendingTickets.change)}`
              : "",
          // trendText: 'previous data',
          icon: <PendingTicketsIcon fontSize="small" />,
          trendPositive: data?.pendingTickets?.change < 0 ? false : true,
          tooltipText: 'Number of unresolved support or user-submitted queries currently awaiting admin action.',
          statsType:"pending_tickets",
          navigateUrl:"/QueryTicketManagement",
          filters:{activeVerificationTab:"Pending Requests"},
        },
        {
          title: "VERIFICATION PENDING",
          mainValue: (data?.verificationPending?.document || 0) + (data?.verificationPending?.selfie || 0) || 0,
          subData: [
            { label: "Document", value: data?.verificationPending?.document || 0 },
            { label: "Selfie", value: data?.verificationPending?.selfie || 0 },
          ],
          icon: <VerificationIcon fontSize="small" />,
          noTrend: true,
          tooltipText: 'Total users whose verification is incomplete. Includes pending document and selfie checks.',
          statsType:"verification_pending",
          navigateUrl:"/verification",
          filters:{activeVerificationTab:"Pending Requests"},
        },
        {
          title: "TOTAL SUBSCRIPTION",
          mainValue: data?.totalSubscription?.value || 0,
          trend: data?.totalSubscription?.change > 0
            ? `+${data.totalSubscription.change}`
            : data?.totalSubscription?.change < 0
              ? `-${Math.abs(data.totalSubscription.change)}`
              : "",

          icon: <PaymentIcon fontSize="small" />,
          trendPositive: data?.totalSubscription?.change < 0 ? false : true,
          tooltipText: 'Total number of subscription.',
          statsType:null,
          resetFilters:true,
          navigateUrl:"/financials",
          filters:{dateRange:{startDate:moment('2020-01-01'), endDate:moment()}}
        },
        {
          title: "TODAY SUBSCRIPTION",
          mainValue: data?.todaySubscription?.value || 0,
          trend: data?.todaySubscription?.change > 0
            ? `+${data.todaySubscription.change}`
            : data?.todaySubscription?.change < 0
              ? `-${Math.abs(data.todaySubscription.change)}`
              : "",

          icon: <PaymentIcon fontSize="small" />,
          trendPositive: data?.todaySubscription?.change < 0 ? false : true,
          tooltipText: 'Total number of subscriptions today',
          statsType:"today_subscription",
          navigateUrl:"/financials",
          filters:{dateRange:{startDate:moment(), endDate:moment()}}
        },
        {
          title: "YESTERDAY SUBSCRIPTION",
          mainValue: data?.yesterdaySubscription?.value || 0,
          trend: data?.yesterdaySubscription?.change > 0
            ? `+${data.yesterdaySubscription.change}`
            : data?.yesterdaySubscription?.change < 0
              ? `-${Math.abs(data.yesterdaySubscription.change)}`
              : "",

          icon: <PaymentIcon fontSize="small" />,
          trendPositive: data?.yesterdaySubscription?.change < 0 ? false : true,
          tooltipText: 'Total number of subscriptions yesterday',
          statsType:"yesterday_subscription",
          navigateUrl:"/financials",
          filters:{dateRange:{startDate:moment().subtract(1, 'days'), endDate:moment().subtract(1, 'days')}}
        },
      ];
      setCardsData(responseSet);
      setIsLoading(false);
    } catch (err) {
      console.error('Error while loading user data', err);
    }
  };

const fetchUnreadBotStatsData = async () => {
  try {
    setChatCardsData(prev => ({
      ...prev,
      mainValue: "Loading...",
      navigateUrl: ""
    }));

    const data = await fetchDashboardUnreadBotChatStatsData(filter);
    if (!data) return;

    const updatedCard = {
      mainValue: data?.unreadBotChats?.value || 0,
      trend:
        data?.unreadBotChats?.change > 0
          ? `+${data.unreadBotChats.change}`
          : data?.unreadBotChats?.change < 0
          ? `-${Math.abs(data.unreadBotChats.change)}`
          : "",
      trendPositive: data?.unreadBotChats?.change >= 0,
      navigateUrl: "/chat",
    };

    setChatCardsData(prev => ({ ...prev, ...updatedCard }));

    setCardsData(prev =>
      prev.map(card =>
        card.title === "UNREAD BOT CHATS"
          ? { ...card, ...updatedCard }
          : card
      )
    );

  } catch (err) {
    console.error('Error while loading unread bot stats', err);
  }
};

  useEffect(() => {
    fetchData();
    fetchUnreadBotStatsData()
  }, [JSON.stringify(filter)]);

  const handleNaviagtion = (card)=>{
    if(card?.navigateUrl){
      if(card?.resetFilters){
        dispatch(setResetFilter())
      }
      dispatch(setStatsTypeFilter({
        statsTitle: card?.title,
        statsType: card?.statsType ?? null,
        profileCompilation: null,
        searchTerm: null,
        ...(card?.filters ?? {}),
      }))
      navigate(card?.navigateUrl)
    }
  }

useEffect(() => {
  if (!cardsData?.length) return;

  setCardsData(prev =>
    prev.map(card =>
      card.title === "UNREAD BOT CHATS"
        ? { ...card, ...chatCardsData }
        : card
    )
  );
}, [chatCardsData]);
  return (
    <Grid container spacing={2} py={2}>  
        {isLoading
      ? // Show 8 skeleton cards while loading
        [...Array(8)].map((_, i) => <DashboardCardSkeleton key={i} />)
      : // Show actual data cards
        cardsData.map((card, i) => {
            const positive = card.trendPositive !== false;

        return (
          <Grid item xs={12} sm={6} md={3} key={i} style={{cursor:card?.navigateUrl ? "pointer" : "default"}} onClick={()=>handleNaviagtion(card)}>
            <Tooltip title={card.tooltipText || ""} arrow>
              <Paper
                elevation={1}
                sx={{
                  borderRadius: 2,
                  p: 2,
                  minHeight: 120,
                  height:'100%',
                  display: "flex", transition:'0.3s',
                  flexDirection: "column",
                  justifyContent: "space-between",
                  boxShadow: 'none',
                  border: '1px solid #E8E8E8',
                   "&:hover": {
        boxShadow: "0 0 10px rgba(0,0,0,0.3)",
        transform: "translateY(-3px)",
        border:'1px solid #ff4A4A',
      },
                }}



              >
                {/* Title and Icon */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 1,
                  }}
                >
                  <Typography
                    variant="caption"
                    color="#3C4043"
                    sx={{ fontWeight: 500, fontSize: '14px', }}
                  >
                    {card.title}
                  </Typography>
                  <Box sx={{ color: "#3C4043" }}>{card.icon}</Box>
                </Box>

                {/* Main Value */}
                <Typography sx={{ fontWeight: 500, color: '#000', fontSize: '20px', }}>
                  {card.mainValue}
                </Typography>

                {/* Sub Data (like Document, Selfie) */}
                {card.subData && (
                  <Stack spacing={0.5} mt={1}>
                    {card.subData.map((item, idx) => (
                      <Box
                        key={idx}
                        sx={{ display: "flex", justifyContent: "space-between" }}
                      >
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ fontSize: 12 }}
                        >
                          {item.label}
                        </Typography>
                        <Typography variant="body2" sx={{ fontSize: 12 }}>
                          {item.value}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                )}

                {/* Trend + Arrow */}
                {!card.noTrend && (
                  <Box
                    sx={{
                      mt: card.subData ? 2 : 1,
                      display: "flex",
                      alignItems: "start",
                      flexDirection: 'column',
                    }}
                  >
                    {card.trendText && <TrendText trend={card.trend} trendText={card.trendText} positive={positive} />}
                    {/* <ArrowForwardOutlinedIcon
                      sx={{
                        ml: 1,
                        fontSize: 18,
                        color: positive
                          ? '#2E80DB'
                          : '#2E80DB',
                      }}
                    /> */}
                  </Box>
                )}
              </Paper>
            </Tooltip>
          </Grid>
         );
        })}
    </Grid>
  );
}
